<template>
  <div id="magazine" class="magazine">
    <Breadcrumbs :breadcrumbs="breadcrumbs" class="mt-4" />
    <!-- Magazine Header -->
    <div class="mt-4 mb-6">
      <h1 class="text-h4 font-bold text-gray-900 mb-4">
        {{ $i18n.t('magazine.title') }}
      </h1>

      <!-- Magazine Category Links Component -->
      <MagazineCategoryLinks />
    </div>
    <MagazineHomePaginatedContent />

    <MagazineFooterLinks />
  </div>
</template>

<script setup lang="ts">
import { magazineHomeGetters } from '~/utils/magazineHomeGetters'
import { articleGetters } from '~/utils/articleGetters'

const getFullUrl = useFullLocalizedUrl()

const $i18n = useI18n()
const { locale } = $i18n

const { data: magazineHome } = await useStrapiSingleType<any>(
  `magazin-home?locale=${locale.value}&populate[summary_list][populate]=*&populate[seo][populate]=*`
)

const breadcrumbs = ref([
  {
    text: $i18n.t('general.home'),
    link: getFullUrl('/'),
  },
  {
    text: $i18n.t('magazine.magazine'),
    link: getFullUrl('/magazine'),
  },
])

const seoOptions = computed(() => ({
  allowedParams: [],
  shouldBeIndexed: true,
  meta: {
    title:
      magazineHomeGetters.getSeoTitle(magazineHome?.value?.data) ||
      $i18n.t('general.titles.magazine-slug', {
        article: articleGetters.getTitle(magazineHome?.value?.data),
      }) +
        ' | ' +
        $i18n.t('general.titles.base'),
    'og:title':
      magazineHomeGetters.getSeoTitle(magazineHome?.value?.data) ||
      $i18n.t('general.titles.magazine-slug', {
        article: articleGetters.getTitle(magazineHome?.value?.data),
      }) +
        ' | ' +
        $i18n.t('general.titles.base'),
    description:
      magazineHomeGetters.getSeoMetaDescription(magazineHome?.value?.data) ||
      $i18n.t('general.descriptions.magazine-slug', {
        description: articleGetters.getDescription(magazineHome?.value?.data),
      }),
    'og:description':
      magazineHomeGetters.getSeoMetaDescription(magazineHome?.value?.data) ||
      $i18n.t('general.descriptions.magazine-slug', {
        description: articleGetters.getDescription(magazineHome?.value?.data),
      }),
    ...((magazineHomeGetters.getSeoImageUrl(
      magazineHome?.value?.data,
      'small'
    ) ||
      $i18n.te('general.openGraphImages.magazine-slug')) && {
      'og:image':
        magazineHomeGetters.getSeoImageUrl(
          magazineHome?.value?.data,
          'small'
        ) || $i18n.t('general.openGraphImages.magazine-slug'),
    }),
  },
  head: {
    title:
      articleGetters.getSeoTitle(magazineHome?.value?.data) ||
      $i18n.t('general.titles.magazine-slug', {
        article: articleGetters.getTitle(magazineHome?.value?.data),
      }),
  },
}))

definePageMeta({
  contentGroup: 'magazine',
  preloaderType: 'magazineHome',
})

useSeoHelper(seoOptions)
</script>
